import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/ui/Header';
import Footer from '../../components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { useAuth } from '../../contexts/AuthContext';
import HybridCreditService from '../../services/hybridCreditService';
import CreditPackPurchase from '../../components/CreditPackPurchase';
import { supabase } from '../../lib/supabase';
import { format, formatDistanceToNowStrict, parseISO, endOfMonth } from 'date-fns';
import AdSense from '../../components/AdSense';

const CreditsCenter = () => {
  const navigate = useNavigate();
  const { user, userProfile, isPremium } = useAuth();
  
  // State management
  const [creditBreakdown, setCreditBreakdown] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [creditLots, setCreditLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreditPurchase, setShowCreditPurchase] = useState(false);
  const [showCreditLots, setShowCreditLots] = useState(false);
  
  // Create new assessment state
  const [selectedOccupation, setSelectedOccupation] = useState(null);
  const [selectedSeniority, setSelectedSeniority] = useState('');
  const [selectedLotId, setSelectedLotId] = useState('');
  const [occupations, setOccupations] = useState([]);
  const [showCreateAssessment, setShowCreateAssessment] = useState(false);
  const [creatingAssessment, setCreatingAssessment] = useState(false);

  const seniorityLevels = [
    { value: 'apprentice', label: 'Apprentice' },
    { value: 'junior', label: 'Junior' },
    { value: 'mid', label: 'Mid-Level' },
    { value: 'senior', label: 'Senior' },
    { value: 'executive', label: 'Executive' }
  ];

  // Load all data
  useEffect(() => {
    if (user?.id) {
      loadAllData();
    }
  }, [user?.id]);

  // Handle successful payment redirects
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      // Payment was successful, refresh the data
      if (user?.id) {
        loadAllData();
      }
      // Clean up the URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [user?.id]);

  const loadAllData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Load credit breakdown
      const creditResult = await HybridCreditService.getCreditBreakdown(user.id);
      if (creditResult.success) {
        setCreditBreakdown(creditResult.data);
        setCreditLots([
          ...(creditResult.data.monthlyLots || []),
          ...(creditResult.data.purchasedLots || [])
        ]);
      }

      // Load assessments
      const { data: assessmentsData, error: assessmentsError } = await supabase
        .from('user_assessments')
        .select(`
          id,
          assessment_setup,
          is_completed,
          created_at,
          updated_at
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (assessmentsError) throw assessmentsError;
      setAssessments(assessmentsData || []);

      // Load occupations for dropdown
      const { data: occupationsData, error: occupationsError } = await supabase
        .from('occupations')
        .select('id, title, category_id')
        .eq('is_active', true)
        .order('title');

      if (occupationsError) throw occupationsError;
      setOccupations(occupationsData || []);

    } catch (err) {
      console.error('Error loading credits center data:', err);
      setError('Failed to load credits and assessments data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreditPurchaseSuccess = async (pack, paymentIntent) => {
    console.log('Credit purchase successful:', pack, paymentIntent);
    
    try {
      // Add purchased credits (expire in 3 months)
      const expiresAt = new Date();
      expiresAt.setUTCMonth(expiresAt.getUTCMonth() + 3);
      
      const { success, error } = await HybridCreditService.addCredits(
        user?.id,
        'purchase',
        pack.credits,
        expiresAt.toISOString(),
        { pack_name: pack.name, purchase_simulation: true }
      );
      
      if (success) {
        console.log(`Successfully added ${pack.credits} credits to user account`);
        setShowCreditPurchase(false);
        loadAllData(); // Refresh all data
      } else {
        console.error('Failed to add credits to database:', error);
        setError('Purchase successful but failed to add credits. Please contact support.');
      }
    } catch (err) {
      console.error('Error adding credits:', err);
      setError('Purchase successful but failed to add credits. Please contact support.');
    }
  };

  const handleCreateAssessment = async () => {
    if (!selectedOccupation || !selectedSeniority || !selectedLotId) {
      setError('Please select occupation, seniority, and credit slot');
      return;
    }

    setCreatingAssessment(true);
    setError(null);

    try {
      // Find the selected lot
      const selectedLot = creditLots.find(lot => lot.id === selectedLotId);
      if (!selectedLot) {
        throw new Error('Selected credit lot not found');
      }

      // Create the assessment
      const { data: assessmentData, error: assessmentError } = await supabase
        .from('user_assessments')
        .insert({
          user_id: user.id,
          assessment_setup: {
            occupation: selectedOccupation.title,
            seniority: selectedSeniority,
            category_id: selectedOccupation.category_id,
            is_main: false // This is an additional assessment
          },
          is_completed: false
        })
        .select()
        .single();

      if (assessmentError) throw assessmentError;

      // Consume credit from selected lot
      const { success: consumeSuccess, error: consumeError } = await HybridCreditService.consumeCreditForAssessment(
        user.id,
        assessmentData.id
      );

      if (!consumeSuccess) {
        // If credit consumption failed, delete the assessment
        await supabase.from('user_assessments').delete().eq('id', assessmentData.id);
        throw new Error(consumeError || 'Failed to consume credit');
      }

      // Success - refresh data and navigate
      await loadAllData();
      setShowCreateAssessment(false);
      setSelectedOccupation(null);
      setSelectedSeniority('');
      setSelectedLotId('');
      
      // Navigate to the new assessment
      navigate('/adaptive-questionnaire', {
        state: {
          assessmentSetup: assessmentData.assessment_setup,
          isRetake: false,
          continueExisting: true
        }
      });

    } catch (err) {
      console.error('Error creating assessment:', err);
      setError(err.message || 'Failed to create assessment');
    } finally {
      setCreatingAssessment(false);
    }
  };

  const handleOpenAssessment = async (assessment) => {
    // Navigate to the questionnaire with the existing assessment data
    navigate('/adaptive-questionnaire', {
      state: {
        assessmentSetup: assessment.assessment_setup,
        existingAnswers: assessment.answers,
        existingAttitudes: assessment.attitudes,
        currentQuestionIndex: assessment.current_question_index || 0,
        assessmentId: assessment.id,
        isRetake: false,
        continueExisting: true
      }
    });
  };

  const handleGenerateAIPlan = async (assessment) => {
    // Navigate to AI plan generation with the specific assessment data
    navigate('/ai-plan-generation', {
      state: {
        assessmentSetup: assessment.assessment_setup,
        answers: assessment.answers,
        attitudes: assessment.attitudes,
        assessmentId: assessment.id
      }
    });
  };

  const handleViewExercises = async (assessment) => {
    // Navigate to exercise management with the specific assessment data
    navigate('/exercise-management', {
      state: {
        assessmentSetup: assessment.assessment_setup,
        answers: assessment.answers,
        attitudes: assessment.attitudes,
        assessmentId: assessment.id
      }
    });
  };

  const handleRegenerateAssessment = async (assessmentId) => {
    // Navigate to regenerate the assessment
    navigate('/adaptive-questionnaire', {
      state: {
        assessmentId,
        isRetake: true
      }
    });
  };

  const handleArchiveAssessment = async (assessmentId) => {
    try {
      const { error } = await supabase
        .from('user_assessments')
        .update({ status: 'archived' })
        .eq('id', assessmentId);

      if (error) throw error;
      await loadAllData();
    } catch (err) {
      console.error('Error archiving assessment:', err);
      setError('Failed to archive assessment');
    }
  };

  const getEligibleLots = () => {
    return creditLots.filter(lot => 
      lot.quantity_remaining > 0 && 
      new Date(lot.expires_at) > new Date()
    ).sort((a, b) => new Date(a.expires_at) - new Date(b.expires_at));
  };

  const getSoonestExpiry = () => {
    const eligibleLots = getEligibleLots();
    if (eligibleLots.length === 0) return null;
    return eligibleLots[0].expires_at;
  };

  const formatExpiryDate = (dateString) => {
    const date = parseISO(dateString);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return 'Expired';
    if (diffDays === 0) return 'Expires today';
    if (diffDays === 1) return 'Expires tomorrow';
    if (diffDays <= 7) return `Expires in ${diffDays} days`;
    if (diffDays <= 30) return `Expires in ${Math.ceil(diffDays / 7)} weeks`;
    return `Expires ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        <main className="flex-1 pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center py-16">
              <div className="text-center space-y-4">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                <p className="text-sm text-muted-foreground">Loading credits and assessments...</p>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const totalCredits = (creditBreakdown?.snapshot?.monthly_credits_available || 0) + 
                      (creditBreakdown?.snapshot?.purchased_credits_available || 0);
  const monthlyCredits = creditBreakdown?.snapshot?.monthly_credits_available || 0;
  const purchasedCredits = creditBreakdown?.snapshot?.purchased_credits_available || 0;
  const eligibleLots = getEligibleLots();
  const soonestExpiry = getSoonestExpiry();

  return (
    <>
      <Helmet>
        <title>Credits & Assessments Center - CareerLift</title>
        <meta name="description" content="Manage your credits, see expirations, and create assessments" />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <Header />
        
        <main className="flex-1 pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Page Header */}
            <div className="text-center mb-2">
              <div className="flex items-center justify-center space-x-2 mb-4">
                <Icon name="Coins" size={32} className="text-primary" />
                <h1 className="text-3xl font-bold text-foreground">
                  Credits & Assessments Center
                </h1>
              </div>
              <div className="flex justify-center mb-2">
                <audio
                  src="/assets/audio/Credits.mp3"
                  controls
                  preload="metadata"
                  className="h-8 max-w-xs"
                />
              </div>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-4">
                Manage your credits, see expirations, and create assessments.
              </p>
              <div className="flex items-center justify-center space-x-4">
                <Badge variant={isPremium ? 'default' : 'secondary'}>
                  Plan: {isPremium ? 'Premium' : 'Free'}
                </Badge>
                <Badge variant="outline">
                  Total Credits: {totalCredits}
                </Badge>
              </div>
            </div>

            {/* AdSense Banner */}
            <div className="mb-2">
              <AdSense adSlot="2014575505" />
            </div>

            {/* Error Display */}
            {error && (
              <Card className="mb-6 border-destructive">
                <CardContent className="p-4">
                  <div className="flex items-center space-x-2 text-destructive">
                    <Icon name="AlertTriangle" size={20} />
                    <span className="font-medium">Error:</span>
                    <span>{error}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Balances Panel */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Icon name="Calendar" size={20} />
                    <span>Monthly Credits</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-primary mb-2">
                    {monthlyCredits} of {isPremium ? '3' : '1'} left
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">
                    Your monthly credits will expire on <span className="text-red-600 font-semibold">{format(endOfMonth(new Date()), 'MMMM dd, yyyy')}</span>.<br />
                    Unused credits <span className="font-bold">DO NOT</span> roll over to the next billing cycle.
                  </p>
                  <div className="text-xs text-muted-foreground">
                    <Icon name="Info" size={12} className="inline-block mr-1" />
                    Monthly credits reset at the end of each month
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Icon name="ShoppingBag" size={20} />
                    <span>Purchased Credits</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-indigo-500 mb-2">
                    {purchasedCredits} available
                  </div>
                  <div className="space-y-1 mb-4">
                    {getEligibleLots().filter(lot => lot.source === 'purchase').map((lot, index) => (
                      <p key={lot.id} className="text-sm text-muted-foreground">
                        {lot.quantity_remaining} credits will expire on <span className="text-red-600 font-semibold">{format(parseISO(lot.expires_at), 'MMMM dd, yyyy')}</span>
                      </p>
                    ))}
                    {getEligibleLots().filter(lot => lot.source === 'purchase').length === 0 && (
                      <p className="text-sm text-muted-foreground">No purchased credits available</p>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    <Icon name="Info" size={12} className="inline-block mr-1" />
                    Purchased credits expire 3 months after purchase. We use the soonest-expiring credits first.
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-4 mb-6">
              <Button onClick={() => setShowCreditPurchase(true)}>
                <Icon name="Plus" size={16} className="mr-2" />
                Buy Credits
              </Button>
              {!isPremium && (
                <Button variant="outline" onClick={() => navigate('/subscription-management')}>
                  <Icon name="Crown" size={16} className="mr-2" />
                  Upgrade to Premium
                </Button>
              )}
            </div>

            {/* Credit Lots Table (Collapsible) */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Credit Details</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCreditLots(!showCreditLots)}
                  >
                    <Icon name={showCreditLots ? "ChevronUp" : "ChevronDown"} size={16} className="mr-2" />
                    {showCreditLots ? 'Hide' : 'Show'} Details
                  </Button>
                </CardTitle>
              </CardHeader>
              {showCreditLots && (
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Source</th>
                          <th className="text-left py-2">Granted</th>
                          <th className="text-left py-2">Expires</th>
                          <th className="text-left py-2">Qty</th>
                          <th className="text-left py-2">Remaining</th>
                          <th className="text-left py-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {creditLots.map((lot) => {
                          const isExpired = new Date(lot.expires_at) < new Date();
                          const isEmpty = lot.quantity_remaining === 0;
                          return (
                            <tr key={lot.id} className="border-b">
                              <td className="py-2">
                                <Badge variant={lot.source === 'monthly_grant' ? 'default' : 'secondary'}>
                                  {lot.source === 'monthly_grant' ? 'Monthly' : 'Purchased'}
                                </Badge>
                              </td>
                              <td className="py-2 text-muted-foreground">
                                {new Date(lot.granted_at).toLocaleDateString()}
                              </td>
                              <td className="py-2 text-muted-foreground">
                                {formatExpiryDate(lot.expires_at)}
                              </td>
                              <td className="py-2">{lot.quantity}</td>
                              <td className="py-2">{lot.quantity_remaining}</td>
                              <td className="py-2">
                                <Badge variant={isExpired || isEmpty ? 'destructive' : 'default'}>
                                  {isExpired ? 'Expired' : isEmpty ? 'Empty' : 'Active'}
                                </Badge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>


            {/* Assessments List */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Your Assessments</CardTitle>
                <CardDescription>
                  Manage your career assessments and create new ones
                </CardDescription>
              </CardHeader>
              <CardContent>
                {assessments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="ClipboardList" size={48} className="mx-auto mb-4" />
                    <p className="mb-4">
                      {!isPremium 
                        ? "You can create your first assessment for your primary occupation."
                        : "Use one credit to explore a new occupation or scenario."
                      }
                    </p>
                    <Button onClick={() => setShowCreateAssessment(true)}>
                      <Icon name="Plus" size={16} className="mr-2" />
                      Create Assessment
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {assessments.map((assessment) => {
                      const occupationTitle = assessment.assessment_setup?.occupation || 'Unknown Occupation';
                      const seniorityLevel = assessment.assessment_setup?.seniority || 'Unknown Level';
                      const isMain = assessment.assessment_setup?.is_main || false;
                      const status = assessment.is_completed ? 'completed' : 'active';
                      
                      return (
                        <div key={assessment.id} className="flex items-center justify-between p-4 border rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <h3 className="font-medium">{occupationTitle}</h3>
                              {isMain && (
                                <Badge variant="default">Main</Badge>
                              )}
                              <Badge variant={status === 'active' ? 'default' : 'secondary'}>
                                {status}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {seniorityLevel} • Created {new Date(assessment.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              variant="default"
                              onClick={() => handleOpenAssessment(assessment)}
                            >
                              <Icon name="ExternalLink" size={14} className="mr-1" />
                              {assessment.is_completed ? 'View' : 'Continue'}
                            </Button>
                            {assessment.is_completed && (
                              <>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => handleGenerateAIPlan(assessment)}
                                >
                                  <Icon name="Sparkles" size={14} className="mr-1" />
                                  AI Plan
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleViewExercises(assessment)}
                                >
                                  <Icon name="Play" size={14} className="mr-1" />
                                  Exercises
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRegenerateAssessment(assessment.id)}
                            >
                              <Icon name="RefreshCw" size={14} className="mr-1" />
                              Regenerate
                            </Button>
                            {!isMain && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleArchiveAssessment(assessment.id)}
                              >
                                <Icon name="Archive" size={14} className="mr-1" />
                                Archive
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Create New Assessment */}
            {showCreateAssessment && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>Create New Assessment</CardTitle>
                  <CardDescription>
                    Use a credit to create an assessment for a different occupation
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Occupation Selector */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Occupation</label>
                      <select
                        value={selectedOccupation?.id || ''}
                        onChange={(e) => {
                          const occupation = occupations.find(occ => occ.id === e.target.value);
                          setSelectedOccupation(occupation);
                        }}
                        className="w-full px-3 py-2 border border-input rounded-md bg-background"
                      >
                        <option value="">Select an occupation...</option>
                        {occupations.map((occupation) => (
                          <option key={occupation.id} value={occupation.id}>
                            {occupation.title}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Seniority Selector */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Seniority Level</label>
                      <select
                        value={selectedSeniority}
                        onChange={(e) => setSelectedSeniority(e.target.value)}
                        className="w-full px-3 py-2 border border-input rounded-md bg-background"
                      >
                        <option value="">Select seniority level...</option>
                        {seniorityLevels.map((level) => (
                          <option key={level.value} value={level.value}>
                            {level.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Credit Slot Selector */}
                    {eligibleLots.length > 0 && (
                      <div>
                        <label className="block text-sm font-medium mb-2">Choose which credit to use</label>
                        <select
                          value={selectedLotId}
                          onChange={(e) => setSelectedLotId(e.target.value)}
                          className="w-full px-3 py-2 border border-input rounded-md bg-background"
                        >
                          <option value="">Select credit slot...</option>
                          {eligibleLots.map((lot) => (
                            <option key={lot.id} value={lot.id}>
                              {lot.source === 'monthly_grant' ? 'Monthly' : 'Purchased'} — 
                              expires {formatExpiryDate(lot.expires_at)} (remaining: {lot.quantity_remaining})
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {eligibleLots.length === 0 && (
                      <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
                        <div className="flex items-center space-x-2 text-warning">
                          <Icon name="AlertTriangle" size={16} />
                          <span className="font-medium">No credits available</span>
                        </div>
                        <p className="text-sm text-warning mt-1">
                          You need to purchase credits to create additional assessments.
                        </p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-4">
                      <Button
                        onClick={handleCreateAssessment}
                        disabled={!selectedOccupation || !selectedSeniority || !selectedLotId || creatingAssessment || eligibleLots.length === 0}
                      >
                        {creatingAssessment ? (
                          <>
                            <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                            Creating...
                          </>
                        ) : (
                          <>
                            <Icon name="Plus" size={16} className="mr-2" />
                            Use 1 Credit & Create
                          </>
                        )}
                      </Button>
                      <Button variant="outline" onClick={() => setShowCreateAssessment(false)}>
                        Cancel
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </main>

        {/* Bottom AdSense Banner */}
        <div className="mb-2">
          <AdSense adSlot="2014575505" />
        </div>

        <Footer />

        {/* Credit Purchase Modal */}
        {showCreditPurchase && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-background rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-2xl font-bold">Buy Credits</h3>
                    <p className="text-muted-foreground">Purchase Assessment Credits</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCreditPurchase(false)}
                  >
                    <Icon name="X" size={16} />
                  </Button>
                </div>
                <CreditPackPurchase
                  userId={user?.id}
                  onPurchaseSuccess={handleCreditPurchaseSuccess}
                  onPurchaseError={(error) => setError(error)}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default CreditsCenter;
